import {
    Button,
    DataGrid,
    Form,
    GridColumnsConfigProvider,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { addComment } from 'axios/orderApi';
import DataTimeText from 'components/Common/DataTime';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { OrderComments } from 'models/api/order';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Container, FormGroup } from 'reactstrap';
import '../OrderInfo.scss';

const CommentsTab = ({
    commentsQueryFn,
    isOpen,
    orderId,
}: {
    commentsQueryFn: () => Promise<OrderComments[]>;
    isOpen: boolean;
    orderId: number;
}) => {
    const queryClient = useQueryClient();
    const { hasRole } = useAuth();
    const api = useApi();
    const [comments, setComments] = useState<OrderComments[] | null>();
    const [wasOpened, setWasOpened] = useState(false);
    useTranslation();

    useEffect(() => {
        !wasOpened && setWasOpened(isOpen);
    }, [isOpen]);

    const commentsQuery = useQuery('comments', () => commentsQueryFn(), {
        onSuccess: (data) => setComments(data),
        enabled: wasOpened,
    });

    const addCommentMutation = useMutation(
        'addCommentMutation',
        (comment: string) => addComment(api, orderId, comment),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['comments']);
            },
        }
    );

    return (
        <Container>
            {comments && comments.length > 0 && (
                <FormGroup>
                    <GridColumnsConfigProvider<OrderComments>
                        storagePath="comments-grid"
                        initColumns={[
                            {
                                key: 'dt',
                                draggable: false,
                                name: <Trans i18nKey={'BackOfficeDate'} />,
                                formatter: ({ row }) => <DataTimeText value={row.dt} />,
                            },
                            {
                                key: 'comment',
                                draggable: false,
                                name: <Trans i18nKey={'BackOfficeComment'} />,
                            },
                            {
                                key: 'userFullname',
                                draggable: false,
                                name: <Trans i18nKey={'BackOfficeOperator'} />,
                            },
                        ]}
                    >
                        <DataGrid<OrderComments>
                            reactDataGreedProps={{
                                defaultColumnOptions: {
                                    sortable: false,
                                    resizable: false,
                                },
                            }}
                            rows={comments}
                            loading={commentsQuery.isFetching}
                            blockSize="100%"
                            minBlockSize={90}
                        />
                    </GridColumnsConfigProvider>
                </FormGroup>
            )}
            {hasRole('orderAddComment') && (
                <Form<{ comment: string }>
                    onSubmit={(data) => addCommentMutation.mutate(data.comment)}
                    defaultValues={{ comment: '' }}
                >
                    <ValidatedInput name="comment" type="textarea" isFloating={false} />
                    <FormGroup>
                        <Button
                            color="secondary"
                            type="submit"
                            isPending={addCommentMutation.isLoading}
                        >
                            <Trans i18nKey="BackOfficeWriteComment" />
                        </Button>
                    </FormGroup>
                </Form>
            )}
        </Container>
    );
};

export default CommentsTab;
